import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { createStore, Store } from 'effector';
import { applyTimeZone, RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { Transaction, createPaginatedSchema, transactionSchema } from '../types';

export type OriginalTransactionRegistryParams = {
	page?: number;
	limit?: number;
	dateFrom?: string;
	dateTo?: string;
	timeZone?: number;
	currencies?: string[];
};

export type TransactionRegistryFilters = {
	dateFrom: string;
	dateTo: string;
	timeZone: number;
	currencies: string[];
};

export type TransactionRegistryParams = RegistryParams<TransactionRegistryFilters>;

export type TransactionRegistryQuery = RegistryQuery<
	Transaction,
	TransactionRegistryFilters,
	JsonApiRequestError
>;

export const createTransactionRegistryQuery = (
	$accountId: Store<number | null>,
): TransactionRegistryQuery =>
	createAuthQuery({
		params: declareParams<TransactionRegistryParams>(),
		request: {
			method: 'GET',
			url: {
				source: $accountId ?? createStore(null),
				fn: (_, id) => `/account/transactions/${id}`,
			},
			query: (params): OriginalTransactionRegistryParams => ({
				page: params.page,
				limit: params.limit,
				dateFrom: params.filters.dateFrom,
				dateTo: params.filters.dateTo,
				currencies: params.filters.currencies,
				timeZone: params.filters.timeZone,
			}),
		},
		response: {
			contract: zodContract(createPaginatedSchema(transactionSchema)),
			mapData: ({ result, params }) => ({
				...result,
				rows: result.rows.map((row) => ({
					...row,
					createdAt: applyTimeZone(row.createdAt, params.filters.timeZone),
				})),
			}),
		},
	});
